import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Users, Target, CalendarRange, MapPinned } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import DateRangeBar, { RANGE_PRESETS } from "@/components/DateRangeBar";
import ReportDrillDialog from "@/components/ReportDrillDialog";

const monthRange = () => { const [start, end] = RANGE_PRESETS[2].range(); return { preset: "month", start, end }; };

export default function TeamReportsPage() {
  const [range, setRange] = useState(monthRange);
  const [drill, setDrill] = useState(null);
  const { start, end } = range;
  const valid = start && end && start <= end;

  const q = useQuery({
    queryKey: ["reports", "team", start, end],
    queryFn: async () => (await api.get("/reports/team", { params: { start, end } })).data,
    enabled: Boolean(valid),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const statuses = d?.statuses || [];
  const open = (m, metric) => setDrill({ user_id: m.user_id, name: m.name, metric });

  return (
    <div className="space-y-5" data-testid="team-reports-page">
      <div>
        <h2 className="text-2xl font-bold text-navy lg:text-3xl">Reports</h2>
        <p className="mt-1 text-sm text-slate-500">Team performance for any period. Tap any member's number to see the leads behind it.</p>
      </div>

      <div className="soft-card p-4" data-testid="reports-date-filter">
        <DateRangeBar value={range} onChange={setRange} prefix="reports" />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="reports-summary">
        <Summary tid="reports-total-assigned" label="Leads assigned" value={d?.totals.leads_assigned} Icon={Users} />
        <Summary tid="reports-total-opps" label="Opportunities" value={d?.totals.opportunities} Icon={Target} />
        {statuses.map((s) => (
          <Summary key={s} tid={`reports-total-${s}`} label={STATUS_META[s]?.label} value={d?.totals.statuses[s]} color={STATUS_META[s]?.color} />
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 lg:hidden" data-testid="reports-member-cards">
        {(d?.items || []).map((m) => <MemberCard key={m.user_id} m={m} statuses={statuses} onOpen={open} />)}
      </div>

      <div className="soft-card hidden overflow-x-auto lg:block" data-testid="reports-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Team member</th>
              <th className="px-3 py-3 text-right">Leads assigned</th>
              <th className="px-3 py-3 text-right">Opportunities</th>
              {statuses.map((s) => <th key={s} className="px-3 py-3 text-right">{STATUS_META[s]?.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {(d?.items || []).map((m) => (
              <tr key={m.user_id} className="border-b border-slate-50 transition-colors hover:bg-slate-50" data-testid={`reports-row-${m.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-navy">{m.name}</div>
                  <div className="text-[11px] capitalize text-slate-400">{m.role}{m.is_active === false ? " · inactive" : ""}</div>
                </td>
                <Num v={m.leads_assigned} bold onClick={() => open(m, "leads_assigned")} tid={`reports-cell-${m.user_id}-leads_assigned`} />
                <Num v={m.opportunities} bold onClick={() => open(m, "opportunities")} tid={`reports-cell-${m.user_id}-opportunities`} />
                {statuses.map((s) => <Num key={s} v={m.statuses[s]} onClick={() => open(m, s)} tid={`reports-cell-${m.user_id}-${s}`} />)}
              </tr>
            ))}
          </tbody>
          {d && (
            <tfoot>
              <tr className="bg-slate-50 font-bold text-navy" data-testid="reports-row-total">
                <td className="px-4 py-3">Total</td>
                <Num v={d.totals.leads_assigned} bold />
                <Num v={d.totals.opportunities} bold />
                {statuses.map((s) => <Num key={s} v={d.totals.statuses[s]} bold />)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {d && <SiteVisits items={d.items} total={d.totals.visits_done} onOpen={(m) => open(m, "visits_done")} />}
      {q.isLoading && <div className="text-sm text-slate-400">Loading report…</div>}
      <ReportDrillDialog target={drill} onOpenChange={(v) => !v && setDrill(null)} />
    </div>
  );
}

function SiteVisits({ items, total, onOpen }) {
  const rows = [...items].sort((a, b) => b.visits_done - a.visits_done);
  const max = Math.max(1, ...rows.map((m) => m.visits_done));
  return (
    <section className="soft-card p-4 sm:p-6" data-testid="reports-site-visits">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-lg font-bold text-navy"><MapPinned className="h-5 w-5 text-[hsl(var(--brand-gold))]" /> Site Visits</h3>
        <div className="text-right">
          <div className="text-2xl font-black text-navy" data-testid="reports-visits-total">{total}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">completed</div>
        </div>
      </div>
      <p className="-mt-2 mb-3 text-xs text-slate-500">Visits marked Done whose visit date falls in the selected period. Tap a count to see the visits.</p>
      <ul className="space-y-1">
        {rows.map((m) => (
          <li key={m.user_id}>
            <button
              onClick={() => onOpen(m)}
              data-testid={`reports-visits-${m.user_id}`}
              className="grid w-full grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50"
            >
              <span className="truncate text-sm font-medium text-slate-700">{m.name}</span>
              <span className="h-2 overflow-hidden rounded-full bg-slate-100">
                <span className="block h-full rounded-full bg-[hsl(var(--brand-gold))] transition-[width] duration-500" style={{ width: `${(m.visits_done / max) * 100}%` }} />
              </span>
              <span className={`min-w-[2rem] text-right text-base font-bold tabular-nums ${m.visits_done ? "text-navy" : "text-slate-300"}`}>{m.visits_done}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Summary({ label, value, Icon = CalendarRange, color, tid }) {
  return (
    <div className="soft-card p-3" data-testid={tid}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
        {color ? <span className="h-2 w-2 rounded-full" style={{ background: color }} /> : <Icon className="h-3.5 w-3.5" />}
        <span className="leading-tight">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-black text-navy">{value ?? "—"}</div>
    </div>
  );
}

function MemberCard({ m, statuses, onOpen }) {
  return (
    <div className="soft-card p-4" data-testid={`reports-card-${m.user_id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-navy">{m.name}</div>
        <span className="text-[11px] capitalize text-slate-400">{m.role}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Stat label="Leads assigned" v={m.leads_assigned} onClick={() => onOpen(m, "leads_assigned")} tid={`reports-card-${m.user_id}-leads_assigned`} />
        <Stat label="Opportunities" v={m.opportunities} onClick={() => onOpen(m, "opportunities")} tid={`reports-card-${m.user_id}-opportunities`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
        {statuses.map((s) => (
          <button key={s} onClick={() => onOpen(m, s)} data-testid={`reports-card-${m.user_id}-${s}`}
            className="-mx-1 flex items-center justify-between rounded-md px-1 py-0.5 text-left transition-colors hover:bg-slate-100">
            <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s]?.color }} />{STATUS_META[s]?.label}</span>
            <b className="text-slate-800">{m.statuses[s]}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, v, onClick, tid }) => (
  <button onClick={onClick} data-testid={tid} className="rounded-xl bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-slate-100">
    <div className="text-[11px] text-slate-500">{label}</div><div className="text-xl font-black text-navy">{v}</div>
  </button>
);
const Num = ({ v, bold, onClick, tid }) => (
  <td className="px-1 py-1.5 text-right">
    {onClick ? (
      <button onClick={onClick} data-testid={tid} title="See the leads behind this number"
        className={`rounded-lg px-2 py-1.5 tabular-nums transition-colors hover:bg-[hsl(var(--brand-navy))] hover:text-white ${bold ? "font-bold text-navy" : v ? "text-slate-700" : "text-slate-300"}`}>
        {v}
      </button>
    ) : (
      <span className="px-2 tabular-nums">{v}</span>
    )}
  </td>
);
